import { Box, MenuItem, Select } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { getOrderStatusColor } from '@/utils/orderStatus';

interface StatusSelectProps {
  id?: string;
  ariaLabel?: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  updating?: boolean;
  wrapText?: boolean;
  truncateText?: boolean;
  fontSize?: number;
  isOptionDisabled?: (value: string) => boolean;
}

export const StatusSelect = ({
  id, ariaLabel, value, options, onChange, disabled = false, updating = false, wrapText = false, truncateText = true, fontSize = 12, isOptionDisabled,
}: StatusSelectProps) => {
  const availableOptions = value && !options.includes(value) ? [value, ...options] : options;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0,
      color: 'rgba(0, 0, 0, 0.87)', fontFamily: 'Roboto, sans-serif',
      fontSize, fontWeight: 400, textAlign: 'left', letterSpacing: '.0178571429em',
    }}>
      <Box sx={{ minWidth: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <FiberManualRecordIcon sx={{
          textAlign: 'left', display: 'block', fill: 'currentColor', width: '10px', height: '10px',
          overflow: 'hidden', lineHeight: '10px', userSelect: 'none', fontSize: '10px',
          alignSelf: 'center', color: getOrderStatusColor(value), flexShrink: 0,
        }} />
      </Box>
      <Select<string>
        id={id}
        value={value}
        variant="standard"
        displayEmpty
        disableUnderline
        inputProps={{ 'aria-label': ariaLabel }}
        renderValue={(selected) => String(selected || '*UNMAPPED*')}
        sx={{
          display: 'inline-block', width: '100%', minWidth: 0, outline: 'none', textAlign: 'left',
          whiteSpace: 'nowrap', color: 'rgba(0, 0, 0, 0.87)', fontFamily: 'Roboto, sans-serif',
          fontSize, fontWeight: 400, lineHeight: 1.35, letterSpacing: '.0178571429em', pt: 0, flex: 1,
          '& .MuiInputBase-input': { p: 0 },
          '&& .MuiSelect-select': {
            width: '100%', minWidth: 0, maxWidth: 'none', textAlign: 'left', whiteSpace: wrapText ? 'normal' : 'nowrap', overflow: wrapText || !truncateText ? 'visible' : 'hidden',
            overflowWrap: wrapText ? 'anywhere' : 'normal',
            textOverflow: wrapText || !truncateText ? 'clip' : 'ellipsis', pr: '28px !important', pt: wrapText ? 0 : '6px', pb: 0, display: 'block', boxSizing: 'border-box',
          },
          '& .MuiSelect-icon': { right: '4px', top: '50%', transform: 'translateY(-50%)' },
        }}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || updating}
      >
        {availableOptions.map((status) => (
          <MenuItem
            key={status}
            value={status}
            disabled={isOptionDisabled?.(status)}
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize, fontWeight: 400, lineHeight: '20px' }}
          >
            {status}
          </MenuItem>
        ))}
      </Select>
      {updating && (
        <Box id="status-update-warning" sx={{
          ml: 0.5, px: 0.5, py: 0, color: '#fff', backgroundColor: '#0c479d', fontSize: '8px',
          fontWeight: 700, lineHeight: 1.1, letterSpacing: 0, borderRadius: '2px', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          UPDATING
        </Box>
      )}
    </Box>
  );
};